import { useEffect, useState } from "react";
import {
  WalletApiService,
  WalletBalanceInfoResponseDTO,
} from "@zenith/services/user";

export type RegistrationType =
  WalletBalanceInfoResponseDTO.registrationType | null;

// BOAS-8200/8203/8197/7938: individual, sole-proprietorship, or a legacy
// record with no registrationType at all route to the facial path;
// everything else (multi-signatory business types) uses the existing
// knowledge-factor-only path. This is UX only — the server re-derives
// eligibility at submit and returns 409 regardless, so a wrong guess here
// just means the caller falls back gracefully rather than being unsafe.
const FACIAL_ELIGIBLE_TYPES: ReadonlySet<RegistrationType> = new Set([
  WalletBalanceInfoResponseDTO.registrationType.INDIVIDUAL,
  WalletBalanceInfoResponseDTO.registrationType.SOLE_PROPRIETORSHIP,
  null,
]);

interface UseRegistrationTypeResult {
  registrationType: RegistrationType | undefined;
  /** undefined while still loading */
  isFacialEligible: boolean | undefined;
  isLoading: boolean;
}

export const useRegistrationType = (): UseRegistrationTypeResult => {
  const [registrationType, setRegistrationType] = useState<
    RegistrationType | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    WalletApiService.getWalletDetails()
      .then((response) => {
        if (!isMounted) return;
        setRegistrationType(response.data?.registrationType ?? null);
      })
      .catch((error) => {
        console.error("Failed to load registration type:", error);
        // Fail open to the facial path (the server has the final say and
        // will 409 if it isn't actually applicable) rather than silently
        // blocking a merchant who should have facial available.
        if (isMounted) setRegistrationType(null);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    registrationType,
    isFacialEligible:
      registrationType === undefined
        ? undefined
        : FACIAL_ELIGIBLE_TYPES.has(registrationType),
    isLoading,
  };
};
